"use client";
import { CircleProfilePhoto } from "@cmp-src/map/InteractiveZones/CircleProfilePhoto";
import { getActualSchedule } from "@cmp-src/map/InteractiveZones/helpers";
import type { AuthenticatedUser, IResource } from "@gettactic/api";
import { getVisibleAssignment } from "@gettactic/helpers";
import { cn } from "@gettactic/helpers/src/css/cn";
import {
  InteractiveZone,
  InteractiveZonesInfo
} from "@gettactic/helpers/src/map/types";
import { hasActiveResourceSchedule } from "@gettactic/helpers/src/schedules/hasActiveResourceSchedule";
import { resourceRequiresApproval } from "@gettactic/helpers/src/resources/resourceRequiresApproval";
import { IResourceWithSchedules, useSettings } from "@gettactic/hooks";
import React, { useLayoutEffect, useRef, useState } from "react";
import { useMap } from "@/features/map/MapProvider";
import { MapRoomSelectionBadge } from "./MapRoomSelectionBadge";

type MapViewSvgInteractiveZonesProps = {
  zonesInfo: InteractiveZonesInfo;
  resources: IResourceWithSchedules[];
  totalResources?: IResource[];
  selectedResource: IResource | null;
  resourceSelected?: (id: string) => void;
  openReservation?: (res: IResource) => void;
  highlightResource: (resId: string | null, target: Element | null) => void;
  lastAccessedDeskResourceId: string | null;
  lastAccessedRoomResourceId: string | null;
  authenticatedUser: AuthenticatedUser | undefined;
  officeId?: string;
  startDate?: Date | null;
  colleagues?: string[];
  isMobile?: boolean;
  calendarDates?: Date[];
  focusedCircleRef?: React.MutableRefObject<SVGSVGElement | null>;
  highlightCircleRef?: React.MutableRefObject<SVGSVGElement | null>;
  startHidden?: boolean;
  /** Armed to add one more room to the reservation being composed. */
  isPickingRoom?: boolean;
  /** Rooms of the reservation being composed, in the order they are numbered. */
  selectedRoomIds?: string[];
};

// NOTE: This component is used by screens that do not have an authenticatedUser.
//       Do not add the useAuthenticated hook here.
export const MapViewSvgInteractiveZones = React.forwardRef<
  SVGSVGElement,
  MapViewSvgInteractiveZonesProps
>(
  (
    {
      highlightResource,
      selectedResource,
      openReservation,
      zonesInfo,
      resources,
      totalResources,
      resourceSelected,
      lastAccessedDeskResourceId,
      lastAccessedRoomResourceId,
      authenticatedUser,
      officeId,
      startDate,
      colleagues,
      calendarDates,
      isMobile,
      highlightCircleRef,
      focusedCircleRef,
      startHidden,
      isPickingRoom = false,
      selectedRoomIds
    },
    ref
  ) => {
    const { mapInfo } = zonesInfo;
    const svg = useRef<SVGSVGElement>(null);
    const [chrome96Bug, setChrome96Bug] = useState(false);
    const { isOfficePrivacyEnabled } = useSettings();
    const isPrivacyEnabled = isOfficePrivacyEnabled(officeId);

    const [hoveringCircle, setHoveringCircle] =
      React.useState<SVGElement | null>(null);

    const [focusCircle, setFocusCircle] = React.useState<SVGElement | null>(
      null
    );

    const { events, resourceInReservation, isOnReservationModal } = useMap();

    // Stable ref callbacks to avoid infinite re-render loops.
    // mergeRefs() creates a new function each render; in React 19 a changing
    // ref callback triggers cleanup(null) + set(element), which re-calls
    // setState and loops forever.
    const svgMergedRef = React.useCallback(
      (node: SVGSVGElement | null) => {
        (svg as React.MutableRefObject<SVGSVGElement | null>).current = node;
        if (typeof ref === "function") ref(node);
        else if (ref)
          (ref as React.MutableRefObject<SVGSVGElement | null>).current = node;
      },
      [ref]
    );

    const hoveringCircleMergedRef = React.useCallback(
      (node: SVGGElement | null) => {
        setHoveringCircle(node);
        if (highlightCircleRef)
          highlightCircleRef.current = node as SVGSVGElement | null;
      },
      [highlightCircleRef]
    );

    const focusCircleMergedRef = React.useCallback(
      (node: SVGGElement | null) => {
        setFocusCircle(node);
        if (focusedCircleRef)
          focusedCircleRef.current = node as SVGSVGElement | null;
      },
      [focusedCircleRef]
    );

    const moveCircleToResource = (
      circleElement: SVGElement,
      resource: IResource | null
    ) => {
      if (!circleElement) return;

      if (!resource || !svg.current) {
        circleElement.style.display = "none";
        return;
      }

      const circle = Array.from(
        svg?.current.querySelectorAll(`#interactive-${resource.id} circle`) ??
          []
      ).find((circle) => {
        const style = window.getComputedStyle(circle);
        return style.display !== "none";
      }) as SVGSVGElement | undefined;

      if (!circle) {
        circleElement.style.display = "none";
        return;
      }

      const cx = Number.parseFloat(String(circle.getAttribute("cx") || 0));
      const cy = Number.parseFloat(String(circle.getAttribute("cy") || 0));

      const point = svg.current.createSVGPoint();
      point.x = cx;
      point.y = cy;

      const ctm = circle.getCTM();
      const transformed = point.matrixTransform(ctm || undefined);

      circleElement.setAttribute("style", "display:block");
      circleElement.setAttribute(
        "transform",
        `translate(${(transformed.x || 0) - 37}, ${(transformed.y || 0) - 37})`
      );

      circleElement.style.display = "block";
    };

    React.useEffect(() => {
      const onHighlightUserResource = (resources: IResource[] | null) => {
        if (!hoveringCircle || !svg.current) return;

        svg.current
          .querySelectorAll(".user-highlight-circle")
          .forEach((circle) => {
            circle.remove();
          });

        resources?.forEach((resource) => {
          const circle = hoveringCircle.cloneNode(true) as SVGElement;
          circle.classList.add("user-highlight-circle");
          circle.setAttribute("style", "display:block");
          svg.current?.prepend(circle);
          moveCircleToResource(circle, resource);
          return circle;
        });
      };

      events.register("onHighlightUserResource", onHighlightUserResource);

      return () => {
        events.unregister("onHighlightUserResource", onHighlightUserResource);
      };
    }, [hoveringCircle, zonesInfo.zones]);

    React.useEffect(() => {
      const onHighlightResource = (resource: IResource | null) => {
        if (!hoveringCircle) return;

        moveCircleToResource(hoveringCircle, resource);
      };
      events.register("onHighlightResource", onHighlightResource);

      return () => {
        events.unregister("onHighlightResource", onHighlightResource);
      };
    }, [hoveringCircle, zonesInfo.zones]);

    React.useEffect(() => {
      const onFocusResource = (resource: IResource | null) => {
        if (!focusCircle) return;

        moveCircleToResource(focusCircle, resource);
      };
      events.register("onFocusResource", onFocusResource);

      return () => {
        events.unregister("onFocusResource", onFocusResource);
      };
    }, [focusCircle, zonesInfo.zones]);

    useLayoutEffect(() => {
      if (!svg.current) {
        return;
      }
      // This code fills partially reserved resources
      // adding class disabled to the first n reserved child.
      //
      // We need to do some low level DOM stuff because
      // child of interactive-zones aren't handled by
      // react. We can improve this if we parse the entire
      // SVG in the future.
      //
      svg.current.querySelectorAll(".interactive-zone").forEach((d) => {
        const reserved = Number.parseInt(
          d.getAttribute("data-reserved") || "0",
          10
        );
        d.querySelectorAll("path").forEach((rect, index) => {
          rect.classList.toggle("disabled", index < reserved);
        });
      });

      // After loading the SVG we add a class chrome96Bug to
      // hide svg text nodes, after some ms we remove it
      // so we trigger a new position
      setChrome96Bug(true);
      setTimeout(() => {
        if (svg.current) {
          setChrome96Bug(false);
        }
      }, 200);
    }, [svg, zonesInfo, resources]);

    if (!mapInfo) {
      return null;
    }

    // Temporary function for Instructure contract
    function openReservationIfSchedulable(resId: string) {
      if (!resources) {
        return;
      }
      const res = resources?.find((x) => x.id === resId);
      if (!res) return;
      // Walk-up resources are never reservable from the app — their
      // occupancy is managed externally via the API. Fall back to the
      // read-only select/details flow instead of leaving the click a no-op,
      // same as the map-builder path (useMapBuilderInteractiveZones.ts).
      if (res.is_walkup) {
        resourceSelected?.(resId);
        return;
      }
      if (openReservation) {
        openReservation(res);
      }
    }

    return (
      <svg
        ref={svgMergedRef}
        xmlns="http://www.w3.org/2000/svg"
        {...mapInfo.attrs}
        className={cn("interactive-zone-svg", chrome96Bug && "chrome96Bug")}
        style={
          startHidden
            ? { opacity: 0, transition: "opacity 0.3s ease-in-out" }
            : undefined
        }
      >
        <g
          style={{ display: "none" }}
          ref={hoveringCircleMergedRef}
          className="hh-circle map-pulse-highlight absolute top-0 left-0 hidden overflow-hidden"
        >
          <circle
            cx="37.2041"
            cy="37"
            r="37"
            fill="url(#paint0_radial_1221_74399)"
          />
          <rect
            x="21.21"
            y="21"
            width="32"
            height="32"
            rx="16"
            stroke="#4F48F7"
            strokeWidth="4"
          />
          <defs>
            <radialGradient
              id="paint0_radial_1221_74399"
              cx="0"
              cy="0"
              r="1"
              gradientUnits="userSpaceOnUse"
              gradientTransform="translate(37.2041 37) rotate(90) scale(37)"
            >
              <stop offset="0.655" stopColor="#656CFF" stopOpacity="0.4" />
              <stop offset="1" stopColor="#656CFF" />
            </radialGradient>
          </defs>
        </g>
        <g
          ref={focusCircleMergedRef}
          className="map-pulse hh-circle absolute top-0 left-0 hidden"
        >
          <circle
            cx="37.2041"
            cy="37"
            r="37"
            fill="url(#paint0_radial_1221_74398)"
          />
          <rect
            x="21.21"
            y="21"
            width="32"
            height="32"
            rx="16"
            stroke="#4F48F7"
            strokeWidth="4"
          />
          <defs>
            <radialGradient
              id="paint0_radial_1221_74398"
              cx="0"
              cy="0"
              r="1"
              gradientUnits="userSpaceOnUse"
              gradientTransform="translate(37.2041 37) rotate(90) scale(37)"
            >
              <stop offset="0.655" stopColor="#656CFF" stopOpacity="0.4" />
              <stop offset="1" stopColor="#656CFF" />
            </radialGradient>
          </defs>
        </g>

        {/* biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation> */}
        <defs dangerouslySetInnerHTML={{ __html: mapInfo.defs }} />
        {zonesInfo.zones.map((zone: InteractiveZone) => {
          const res = resources.find((x) => x.id === zone.id);
          const notAssigned = !res;
          const requiresApproval =
            !!res && resourceRequiresApproval(res) && res.available_slots > 0;
          const isWalkUp = !!res?.is_walkup;

          const isMultipleDates = (calendarDates?.length || 0) > 1;
          const disabled = !!res && res.available_slots === 0;
          const reservedSlots = res?.reserved_slots || 0;
          const hasSchedules = hasActiveResourceSchedule(
            res?.schedules,
            isWalkUp
          );
          const schedules = (res?.schedules || []).map((schedule) => ({
            ...schedule,
            user: schedule.user || schedule.slots?.[0]?.user || null
          }));
          const actualSchedule = getActualSchedule(
            schedules as never,
            startDate
          );
          const authUserId = authenticatedUser?.user?.id;
          const scheduledUserId = schedules[0]?.user?.id;
          // Walk-up occupancy never flips `disabled` (see hasSchedules
          // above) — use the same corrected occupancy signal here so the
          // occupant avatar can show for a walk-up check-in too.
          const isOccupied = isWalkUp ? hasSchedules : disabled;
          const showCircleProfilePhoto =
            !isMultipleDates &&
            isOccupied &&
            hasSchedules &&
            actualSchedule &&
            !!res &&
            (res.type === "desk" || res.type === "parking_space") &&
            res.total_slots === 1 &&
            // A walk-up check-in with no real person attached is stamped
            // with a "walkup" placeholder identity — there's no avatar to
            // show for it, so the spot must keep its default circle (styled
            // as unavailable/occupied) instead of hiding it for a photo
            // that will never render, which otherwise left the spot with no
            // painted shape at all — dark and unclickable.
            !actualSchedule.user?.roles?.includes("walkup");

          const assignedUser = getVisibleAssignment(totalResources, zone.id);

          const roomSelectionPosition = selectedRoomIds
            ? selectedRoomIds.indexOf(zone.id) + 1
            : 0;
          // A room the reservation already holds has nothing left to add: it keeps its
          // number badge but stops offering itself while the map is armed.
          const isAlreadyPickedRoom =
            isPickingRoom && roomSelectionPosition > 0;
          const isPickableRoom =
            isPickingRoom &&
            res?.type === "meeting_room" &&
            !isAlreadyPickedRoom;
          // Only rooms can join the reservation being composed — everything else
          // reads as unavailable while the map is armed.
          const isNotSelectable = isPickingRoom && res?.type !== "meeting_room";

          const handleCircleIndicator = () => {
            if (scheduledUserId === authUserId) {
              const indicatorCx = (zone.circle?.cx ?? 0) + 20;
              const indicatorCy = (zone.circle?.cy ?? 0) - 20;
              const counterRotate = zone.rotation
                ? `rotate(${-zone.rotation}, ${zone.circle?.cx ?? 0}, ${zone.circle?.cy ?? 0})`
                : undefined;
              return (
                <g
                  transform={
                    zone?.mapBuilderContainerTransformation ||
                    zone?.containerTransformation
                  }
                >
                  <circle
                    className="animate-pulse text-primary"
                    transform={counterRotate}
                    cx={indicatorCx}
                    cy={indicatorCy}
                    r={10}
                    fill="currentColor"
                    stroke="none"
                  />
                </g>
              );
            }

            return null;
          };

          return (
            <React.Fragment key={`interactive-${zone.id}`}>
              <g
                data-reserved={reservedSlots}
                id={`interactive-${zone.id}`}
                onClick={(ev) => {
                  ev.preventDefault();

                  // While picking a room to add, a click only feeds the selection —
                  // it must not swap the reservation over to the clicked room.
                  if (isPickingRoom) {
                    if (isAlreadyPickedRoom) return;
                    resourceSelected?.(zone.id);
                    return;
                  }

                  if (isOnReservationModal) {
                    highlightResource(zone.id, null);
                  }

                  const isCmdOrCtrl = ev.metaKey || ev.ctrlKey;
                  // Check if Cmd (Mac) or Ctrl (Windows/Linux) key is pressed
                  if (isCmdOrCtrl) {
                    openReservationIfSchedulable(zone.id);
                  }
                  if (resourceSelected && !isCmdOrCtrl) {
                    resourceSelected(zone.id);
                  } else if (
                    openReservation &&
                    !isOnReservationModal &&
                    !isCmdOrCtrl
                  ) {
                    openReservationIfSchedulable(zone.id);
                  } else if (isOnReservationModal) {
                    if (res && !res.is_walkup) {
                      openReservation?.(res);
                    } else if (res?.is_walkup) {
                      resourceSelected?.(zone.id);
                    }
                  }
                }}
                className={cn(
                  isMobile && !res
                    ? "disabled"
                    : cn(
                        !!selectedResource &&
                          selectedResource.id === zone.id &&
                          "selected",
                        isOnReservationModal &&
                          resourceInReservation === zone.id &&
                          "selected",
                        (lastAccessedDeskResourceId === zone.id ||
                          lastAccessedRoomResourceId === zone.id) &&
                          "last-used",
                        "interactive-zone",
                        // Uses isOccupied (not the raw, full-window
                        // `disabled`) so a walk-up's dot styling matches its
                        // real-time occupancy — free right now but booked at
                        // some other point in the queried window must still
                        // read as available, not dark/unavailable.
                        isOccupied || (hasSchedules && isMultipleDates)
                          ? "disabled"
                          : undefined,
                        notAssigned && "notAssigned",
                        requiresApproval && "requires-approval",
                        isWalkUp && !isOccupied && !hasSchedules && "walk-up",
                        !isOccupied &&
                          hasSchedules &&
                          "bookeable-with-schedules",
                        isOccupied &&
                          hasSchedules &&
                          !isMultipleDates &&
                          "not-bookeable-with-schedules",
                        isOccupied &&
                          !hasSchedules &&
                          "not-bookeable-without-schedules",
                        showCircleProfilePhoto && "show-circle-profile-photo",
                        isPickableRoom && "pickable-room",
                        roomSelectionPosition > 0 && "selected-room",
                        isAlreadyPickedRoom && "already-picked-room"
                      ),
                  isNotSelectable && "not-selectable"
                )}
                onMouseEnter={({ currentTarget }) => {
                  if (!res) {
                    return;
                  }
                  highlightResource(res.id, currentTarget);
                }}
                onMouseLeave={() => {
                  highlightResource(null, null);
                }}
              >
                <g
                  className="circle-default"
                  /* biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation> */
                  dangerouslySetInnerHTML={{ __html: zone.html }}
                  transform={zone.mapBuilderContainerTransformation}
                />

                {assignedUser && zone?.circle ? (
                  <CircleProfilePhoto
                    privacy={
                      isPrivacyEnabled &&
                      assignedUser.id !== authenticatedUser?.user?.id
                    }
                    zone={zone}
                    id={res?.id ?? assignedUser.id}
                    profilePhoto={assignedUser.profile_photo_url ?? ""}
                    name={assignedUser.name}
                    hasCheckedIn={false}
                    // Dedicated resources show their person at full
                    // strength — the faded look is only for regular
                    // restricted assignments.
                    restricted={!res?.is_dedicated}
                  />
                ) : null}

                {showCircleProfilePhoto && zone?.circle ? (
                  <CircleProfilePhoto
                    privacy={
                      isPrivacyEnabled &&
                      actualSchedule?.user.id !== authenticatedUser?.user?.id
                    }
                    zone={zone}
                    id={res?.id || ""}
                    profilePhoto={actualSchedule?.user?.profile_photo_url ?? ""}
                    name={actualSchedule?.user.name || ""}
                    hasCheckedIn={actualSchedule?.has_checked_in || false}
                    grayscale={actualSchedule?.user.roles?.includes("deleted")}
                  />
                ) : null}
                {showCircleProfilePhoto && !isMultipleDates
                  ? handleCircleIndicator()
                  : null}

                {/* Numbering only makes sense once the reservation holds more than one room */}
                {(selectedRoomIds?.length ?? 0) > 1 &&
                roomSelectionPosition > 0 ? (
                  <MapRoomSelectionBadge
                    zone={zone}
                    position={roomSelectionPosition}
                  />
                ) : null}
              </g>
            </React.Fragment>
          );
        })}
      </svg>
    );
  }
);
